import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Loader2, Home, ShoppingBag, RefreshCw } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const PaymentStatusPage = () => {
  const { orderId: pathOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Verifying payment...");
  const [orderNumber, setOrderNumber] = useState(null);
  const [actualOrderId, setActualOrderId] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get order_id from query params (Cashfree adds this)
        const cfOrderId = searchParams.get("order_id");
        // Get actual order UUID from path params
        const orderUUID = pathOrderId;
        
        console.log("PaymentStatusPage - cfOrderId:", cfOrderId, "pathOrderId:", orderUUID);
        
        // Store the actual order ID for navigation
        setActualOrderId(orderUUID);
        
        if (cfOrderId) {
          // Extract order number from Cashfree order ID (FLEETY_123456789 -> 123456789)
          const extractedOrderNumber = cfOrderId.replace("FLEETY_", "");
          setOrderNumber(extractedOrderNumber);
          
          // Verify payment status with Cashfree via backend
          console.log("Verifying payment status with Cashfree for:", cfOrderId);
          const response = await axios.get(`${API}/payment/order-status/${cfOrderId}`);
          console.log("Cashfree status response:", response.data);
          
          if (response.data.order_status === "PAID") {
            setStatus("success");
            setMessage("Payment successful! Your order is being prepared.");
            
            // Update order status in our database
            if (orderUUID) {
              try {
                await axios.put(`${API}/order/${orderUUID}/payment-status`, {
                  payment_status: "paid",
                  cf_order_id: cfOrderId
                });
                console.log("Order payment status updated in database");
              } catch (updateError) {
                console.error("Error updating order status:", updateError);
                // Don't fail the flow, payment was successful
              }
            }
          } else if (response.data.order_status === "ACTIVE") {
            // Payment still pending - may need retry
            if (retryCount < 3) {
              setStatus("pending");
              setMessage("Payment is being processed... Please wait.");
            } else {
              setStatus("success");
              setMessage("Payment received! Your order is being processed.");
            }
          } else if (response.data.order_status === "EXPIRED") {
            setStatus("failed");
            setMessage("Payment session expired. Please try again.");
          } else {
            setStatus("failed");
            setMessage("Payment was not completed. Please try again.");
          }
        } else if (orderUUID) {
          // No CF order ID but have order UUID - check our database
          console.log("No Cashfree order ID, checking order status in database");
          try {
            const orderResponse = await axios.get(`${API}/order/track/${orderUUID}`);
            if (orderResponse.data && orderResponse.data.payment_status === "paid") {
              setStatus("success");
              setMessage("Payment confirmed! Your order is being prepared.");
              setOrderNumber(orderResponse.data.order_number);
            } else {
              setStatus("success");
              setMessage("Order placed successfully!");
              setOrderNumber(orderResponse.data?.order_number);
            }
          } catch (orderError) {
            console.error("Error fetching order:", orderError);
            setStatus("success");
            setMessage("Order received! View your order details below.");
          }
        } else {
          // No identifiers at all
          setStatus("success");
          setMessage("Payment completed! View your orders below.");
        }
      } catch (error) {
        console.error("Error verifying payment:", error);
        // Even if verification fails, show success with navigation options
        // This prevents user from being stuck
        setStatus("success");
        setMessage("Payment received! View your order below.");
      }
    };

    verifyPayment();
  }, [pathOrderId, searchParams, retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setStatus("checking");
    setMessage("Verifying payment...");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 size={64} className="mx-auto text-[#3FA9F5] animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-2">{message}</p>
            {orderNumber && (
              <p className="text-lg font-semibold text-[#3FA9F5] mb-6">Order #{orderNumber}</p>
            )}
            <div className="space-y-3">
              {actualOrderId && (
                <Button
                  onClick={() => navigate(`/order-success/${actualOrderId}`)}
                  className="w-full h-12 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold"
                >
                  <ShoppingBag className="mr-2" size={20} />
                  Track Order
                </Button>
              )}
              <Button
                onClick={() => navigate("/orders")}
                variant={actualOrderId ? "outline" : "default"}
                className={actualOrderId 
                  ? "w-full h-12 border-[#3FA9F5] text-[#3FA9F5] hover:bg-[#3FA9F5] hover:text-white"
                  : "w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
                }
              >
                View All Orders
              </Button>
              <Button
                onClick={() => navigate("/home")}
                variant="ghost"
                className="w-full h-12 text-gray-600"
              >
                <Home className="mr-2" size={20} />
                Continue Shopping
              </Button>
            </div>
          </>
        )}
        
        {status === "pending" && (
          <>
            <Loader2 size={64} className="mx-auto text-yellow-500 animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Processing</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500 mb-4">This may take a few moments...</p>
            <Button
              onClick={handleRetry}
              className="bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
            >
              <RefreshCw className="mr-2" size={18} />
              Check Status
            </Button>
          </>
        )}
        
        {status === "failed" && (
          <>
            <XCircle size={64} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/checkout")}
                className="w-full bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate("/home")}
                variant="outline"
                className="w-full"
              >
                Go to Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentStatusPage;
